import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ClockIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline'
import { enquiryAPI } from '../../services/api'
import toast from 'react-hot-toast'

const Contact = () => {
    const [isSubmitted, setIsSubmitted] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm()

    const enquiryMutation = useMutation({
        mutationFn: enquiryAPI.createEnquiry,
        onSuccess: (data) => {
            toast.success('Message sent successfully! We will get back to you soon.')
            reset()
            setIsSubmitted(true)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send message')
        },
    })

    const onSubmit = (data) => {
        const enquiryData = {
            ...data,
            source: 'contact_page',
            consent_terms: 'true',
        }

        enquiryMutation.mutate(enquiryData)
    }

    return (
        <>
            <Helmet>
                <title>Contact Us | Margdarshak</title>
                <meta name="description" content="Get in touch with Margdarshak for education guidance, course information, and admission support. We're here to help you achieve your career goals." />
                <meta name="keywords" content="contact, education guidance, admission help, career counseling, support" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-primary-600 via-secondary-600 to-primary-800 text-white py-16 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                                <span className="text-accent-400">Contact</span> <span className="text-gold-400">Us</span>
                            </h1>
                            <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Get in touch with our education experts for personalized guidance and support
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Contact Form and Info */}
                <section className="py-16">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            {/* Contact Form */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                className="card p-8"
                            >
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>

                                {isSubmitted ? (
                                    <div className="text-center py-8">
                                        <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent Successfully!</h3>
                                        <p className="text-gray-600 mb-6">
                                            Thank you for contacting us. We will get back to you within 24 hours.
                                        </p>
                                        <button
                                            onClick={() => setIsSubmitted(false)}
                                            className="btn-primary"
                                        >
                                            Send Another Message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('name', { required: 'Name is required' })}
                                                    className="input"
                                                    placeholder="Enter your full name"
                                                />
                                                {errors.name && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email *
                                                </label>
                                                <input
                                                    type="email"
                                                    {...register('email', {
                                                        required: 'Email is required',
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: 'Invalid email address'
                                                        }
                                                    })}
                                                    className="input"
                                                    placeholder="Enter your email"
                                                />
                                                {errors.email && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    {...register('phone', {
                                                        required: 'Phone number is required',
                                                        pattern: {
                                                            value: /^[\+]?[1-9][\d]{0,15}$/,
                                                            message: 'Invalid phone number'
                                                        }
                                                    })}
                                                    className="input"
                                                    placeholder="Enter your phone number"
                                                />
                                                {errors.phone && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('city', { required: 'City is required' })}
                                                    className="input"
                                                    placeholder="Enter your city"
                                                />
                                                {errors.city && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Subject
                                            </label>
                                            <select
                                                {...register('subject')}
                                                className="input"
                                            >
                                                <option value="">Select a subject</option>
                                                <option value="course_inquiry">Course Inquiry</option>
                                                <option value="admission_help">Admission Help</option>
                                                <option value="career_guidance">Career Guidance</option>
                                                <option value="technical_support">Technical Support</option>
                                                <option value="general">General</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Message *
                                            </label>
                                            <textarea
                                                {...register('message', { required: 'Message is required' })}
                                                rows={7}
                                                className="input"
                                                placeholder="Tell us how we can help you..."
                                            />
                                            {errors.message && (
                                                <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                                            )}
                                        </div>

                                        <div className="flex items-start space-x-3">
                                            <input
                                                type="checkbox"
                                                id="consent_marketing"
                                                {...register('consent_marketing')}
                                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="consent_marketing" className="text-sm text-gray-600">
                                                I agree to receive updates about courses and educational opportunities via email and WhatsApp
                                            </label>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={enquiryMutation.isPending}
                                            className="btn-primary btn-lg w-full"
                                        >
                                            {enquiryMutation.isPending ? (
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Sending...</span>
                                                </div>
                                            ) : (
                                                'Send Message'
                                            )}
                                        </button>
                                    </form>
                                )}
                            </motion.div>

                            {/* Contact Information */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="space-y-8"
                            >
                                {/* Contact Details */}
                                <div className="card p-8">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>

                                    <div className="space-y-6">
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <PhoneIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                                                <p className="text-gray-600">+91-9810530354</p>
                                                <p className="text-sm text-gray-500">Mon-Sat, 9AM-6PM</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <EnvelopeIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                                                <p className="text-gray-600">info@margdarshak.com</p>
                                                <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <MapPinIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                                                <p className="text-gray-600">
                                                    Lal Kuah, Ghaziabad<br />
                                                    Uttar Pradesh, India<br />
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <ClockIcon className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                                                <p className="text-gray-600">
                                                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                                                    Saturday: 10:00 AM - 4:00 PM<br />
                                                    Sunday: Closed
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                {/* FAQ Link */}
                                <div className="card p-8">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Have Questions?</h2>
                                    <p className="text-gray-600 mb-4">
                                        Check out our frequently asked questions for quick answers to common queries.
                                    </p>
                                    <a
                                        href="/faqs"
                                        className="btn-outline w-full"
                                    >
                                        View FAQs
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default Contact
